import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  GitBranch,
  Loader2,
  Plus,
  RefreshCw,
  XCircle,
} from "lucide-react";
import {
  createWorkflow,
  listWorkflows,
  setComplianceContext,
  updateWorkflowStatus,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { WorkflowItem } from "../../types/compliance";

const WORKFLOW_TYPES = [
  "logistics",
  "customs",
  "vat",
  "invoice",
  "shipment",
  "ecmr",
  "audit",
];

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-600",
  closed: "bg-red-100 text-red-700",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "bg-blue-100 text-blue-700";
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {status}
    </span>
  );
}

export default function WorkflowManager() {
  const { credentials } = useComplianceAuth();
  const isAdmin = credentials?.role === "COMPLIANCE_ADMIN";

  setComplianceContext(
    credentials?.tenantId ?? "default",
    credentials?.role ?? "COMPLIANCE_ADMIN",
  );

  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newType, setNewType] = useState(WORKFLOW_TYPES[0]);
  const [creating, setCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  // Status update
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkflows();
      setWorkflows(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleCreate() {
    setCreating(true);
    setCreateSuccess(null);
    setError(null);
    try {
      const wf = await createWorkflow({
        tenant_id: credentials?.tenantId ?? "default",
        workflow_type: newType,
      });
      setWorkflows((prev) => [wf, ...prev]);
      setCreateSuccess(wf.workflow_id);
      setShowCreate(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create workflow",
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleStatusChange(wf: WorkflowItem, status: "active" | "inactive" | "closed") {
    setUpdatingId(wf.workflow_id);
    setError(null);
    try {
      const updated = await updateWorkflowStatus(wf.workflow_id, {
        tenant_id: credentials?.tenantId ?? "default",
        status,
      });
      setWorkflows((prev) =>
        prev.map((w) => (w.workflow_id === updated.workflow_id ? updated : w)),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update workflow",
      );
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <GitBranch size={24} className="text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            Workflow Manager
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition"
            >
              <Plus size={14} />
              New Workflow
            </button>
          )}
        </div>
      </div>

      {/* Create panel */}
      {showCreate && isAdmin && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
          <h2 className="text-sm font-semibold text-indigo-900 mb-3">
            Create Workflow
          </h2>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Workflow Type
              </label>
              <select
                value={newType}
                aria-label="Workflow type"
                onChange={(e) => setNewType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              >
                {WORKFLOW_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 rounded-lg transition"
            >
              {creating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              Create
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg"
            >
              Cancel
            </button>
          </div>
          {createSuccess && (
            <p className="text-xs text-indigo-700 mt-2 flex items-center gap-1">
              <CheckCircle size={12} />
              Created: {createSuccess}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-4">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Workflow table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-800">
            All Workflows
          </span>
          <span className="text-xs text-gray-500">{workflows.length} total</span>
        </div>

        {loading && workflows.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : workflows.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">
            No workflows found. Create one to get started.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Workflow ID
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Type
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Created
                </th>
                {isAdmin && (
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {workflows.map((wf) => (
                <tr key={wf.workflow_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">
                    {wf.workflow_id}
                  </td>
                  <td className="px-4 py-3 text-gray-800 capitalize">
                    {wf.workflow_type}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={wf.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {new Date(wf.created_at).toLocaleDateString()}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      {updatingId === wf.workflow_id ? (
                        <Loader2
                          size={14}
                          className="animate-spin text-gray-400"
                        />
                      ) : (
                        <div className="flex gap-1.5">
                          {wf.status !== "active" && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleStatusChange(wf, "active")
                              }
                              title="Activate"
                              className="text-green-600 hover:text-green-800"
                            >
                              <CheckCircle size={16} />
                            </button>
                          )}
                          {wf.status === "active" && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleStatusChange(wf, "inactive")
                              }
                              title="Deactivate"
                              className="text-amber-500 hover:text-amber-700"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                          {wf.status !== "closed" && (
                            <button
                              type="button"
                              onClick={() =>
                                void handleStatusChange(wf, "closed")
                              }
                              title="Close permanently"
                              className="text-red-400 hover:text-red-600"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
